/**
 * algo.js — outillage de domaine du thème #12 Algorithmique (4e, 191-200).
 *
 * Réinvestit intégralement `svgScratch` du 5e (piles de blocs, boucle « répéter »
 * en C, blocs conditionnels SI…ALORS…SINON ajoutés au socle, expressions, tracés
 * au stylo) et ajoute les visuels de domaine que le 4e demande :
 *   - `svgQuadrillage` : grille du robot débranché (case départ + orientation +
 *     cases coloriées), corrigé STABLE par construction.
 *   - `figureTortue`, `rectangleCanonique`, `formeCanonique` (ajout 2026-09-23,
 *     revue 193) : tortue du lutin au stylo sur quadrillage, qui dit si deux
 *     scripts tracent la même figure à un déplacement près.
 *
 * Convention robot (source de vérité UNIQUE, re-encodée à l'identique côté
 * solveur) : cases repérées (colonne ; ligne), colonne croît vers l'est,
 * ligne croît vers le nord, origine (1 ; 1) en bas à gauche. Le robot part
 * ORIENTÉ VERS L'EST. « PIVOTE À DROITE » = quart de tour horaire (vu de
 * dessus) ; « PIVOTE À GAUCHE » = quart de tour anti-horaire.
 */

export {
    imgElement, imgChoix, svgScript, svgScripts, svgTraces, svgExpr, svgBlocExpr,
    MOINS, COULEURS_BLOCS, fmtFr,
    // ajouts 2026-09-23 (revue 193) : choix d'un QCM d'images dans un même
    // cadre, texte de remplacement d'un script
    svgScriptsMemeCadre, altScript,
} from '../../5eme/pensee-informatique/svgScratch';

// ─── Simulateur robot (côté GEN — le solveur le ré-implémente) ───

// directions dans l'ordre horaire depuis l'est : +1 = pivote à droite.
export const DIRS_ROBOT = [
    { nom: 'est', dc: 1, dl: 0 },
    { nom: 'sud', dc: 0, dl: -1 },
    { nom: 'ouest', dc: -1, dl: 0 },
    { nom: 'nord', dc: 0, dl: 1 },
];

/**
 * Exécute une liste d'instructions à partir d'une case départ (orienté est).
 * Instructions : 'AVANCE' | 'PIVOTE À DROITE' | 'PIVOTE À GAUCHE' | 'MARQUE'.
 * Retourne { fin:{c,l}, dir, marquees:[{c,l}…] } (marquees dédupliquées, ordre
 * de première coloration).
 */
export const executerRobot = (depart, instructions) => {
    let c = depart.c;
    let l = depart.l;
    let dir = 0; // est
    const marquees = [];
    const vues = new Set();
    instructions.forEach((inst) => {
        if (inst === 'AVANCE') {
            c += DIRS_ROBOT[dir].dc;
            l += DIRS_ROBOT[dir].dl;
        } else if (inst === 'PIVOTE À DROITE') {
            dir = (dir + 1) % 4;
        } else if (inst === 'PIVOTE À GAUCHE') {
            dir = (dir + 3) % 4;
        } else if (inst === 'MARQUE') {
            const cle = `${c};${l}`;
            if (!vues.has(cle)) { vues.add(cle); marquees.push({ c, l }); }
        }
    });
    return { fin: { c, l }, dir, marquees };
};

/** Déplie « répète N fois [corps] » en une liste plate d'instructions. */
export const deplierBoucle = (n, corps) => {
    const out = [];
    for (let i = 0; i < n; i++) out.push(...corps);
    return out;
};

// ─── Tortue du lutin au stylo (ajout 2026-09-23, revue 193) ──
//
// Simule le TRACÉ d'une boucle « répéter n fois [corps] » pour prouver, côté
// gen, qu'un script trace ou ne trace pas une figure donnée (gen_193, carte
// « Quel script trace le rectangle ? »). Corps : liste de { avancer: pas } et
// { tourner: degrés }, ↻ (sens des aiguilles d'une montre) compté positif.
// Virages multiples de 90° et longueurs multiples de `unite` seulement (throw
// sinon : c'est une tortue de quadrillage). Départ en (0 ; 0), orienté vers la
// droite, stylo baissé.
//
// La figure tracée est l'ENSEMBLE des segments d'une unité parcourus (un
// segment repassé compte une fois), ramené à une forme canonique qui ne dépend
// ni de la position ni de l'orientation (les huit isométries du quadrillage,
// puis translation au coin) : deux scripts tracent la même figure, à un
// déplacement près, si et seulement si leurs formes canoniques sont égales.
// Un rectangle tracé à partir de sa largeur, ou avec des virages ↺, a donc la
// même forme que celui tracé à partir de sa longueur avec des virages ↻.

const PAS_TORTUE = [[1, 0], [0, -1], [-1, 0], [0, 1]];   // droite, bas, gauche, haut : l'ordre ↻
const ISOMETRIES_QUADRILLAGE = [
    ([x, y]) => [x, y], ([x, y]) => [-y, x], ([x, y]) => [-x, -y], ([x, y]) => [y, -x],
    ([x, y]) => [-x, y], ([x, y]) => [y, x], ([x, y]) => [x, -y], ([x, y]) => [-y, -x],
];

/** Forme canonique d'une liste de segments unité [[x1, y1], [x2, y2]]. */
export const formeCanonique = (segments) => {
    let meilleure = null;
    ISOMETRIES_QUADRILLAGE.forEach((f) => {
        const segs = segments.map(([a, b]) => [f(a), f(b)]);
        const x0 = Math.min(...segs.map(([a, b]) => Math.min(a[0], b[0])));
        const y0 = Math.min(...segs.map(([a, b]) => Math.min(a[1], b[1])));
        const cles = segs.map(([a, b]) => {
            const p = `${a[0] - x0},${a[1] - y0}`;
            const q = `${b[0] - x0},${b[1] - y0}`;
            return p < q ? `${p}-${q}` : `${q}-${p}`;
        });
        const forme = [...new Set(cles)].sort().join(' ');
        if (meilleure === null || forme < meilleure) meilleure = forme;
    });
    return meilleure;
};

/** Forme canonique de la figure tracée par « répéter n fois [corps] ». */
export const figureTortue = (n, corps, unite = 1) => {
    let x = 0;
    let y = 0;
    let d = 0;
    const segments = [];
    const suite = deplierBoucle(n, corps);
    for (let i = 0; i < suite.length; i++) {
        const ins = suite[i];
        if (ins.tourner !== undefined) {
            if (ins.tourner % 90 !== 0) throw new Error(`figureTortue : virage de ${ins.tourner} degrés hors quadrillage`);
            d = (((d + ins.tourner / 90) % 4) + 4) % 4;
        } else {
            if (ins.avancer % unite !== 0) throw new Error(`figureTortue : ${ins.avancer} n'est pas un multiple de ${unite}`);
            const [dx, dy] = PAS_TORTUE[d];
            for (let k = 0; k < ins.avancer / unite; k++) {
                segments.push([[x, y], [x + dx, y + dy]]);
                x += dx;
                y += dy;
            }
        }
    }
    if (!segments.length) throw new Error('figureTortue : rien n\'est tracé');
    return formeCanonique(segments);
};

/** Forme canonique du contour d'un rectangle a × b (en unités), construit
 *  DIRECTEMENT, sans tortue : le second chemin de la preuve. */
export const rectangleCanonique = (a, b) => {
    const segments = [];
    for (let i = 0; i < a; i++) {
        segments.push([[i, 0], [i + 1, 0]]);
        segments.push([[i, b], [i + 1, b]]);
    }
    for (let j = 0; j < b; j++) {
        segments.push([[0, j], [0, j + 1]]);
        segments.push([[a, j], [a, j + 1]]);
    }
    return formeCanonique(segments);
};

// ─── SVG quadrillage du robot ────────────────────────────────

const FLECHE_DIR = { est: '0,-6 9,0 0,6', sud: '-6,0 0,9 6,0', ouest: '0,-6 -9,0 0,6', nord: '-6,0 0,-9 6,0' };

/**
 * Grille (colonnes × lignes), case départ marquée, orientation en flèche,
 * cases coloriées. `desc` (optionnel) : chaîne machine relue par le solveur
 * (ex. liste des cases coloriées pour un QCM d'images).
 */
export const svgQuadrillage = ({
    cols, rows, depart = null, orientation = 'est', marquees = [],
    showDepart = true, compact = false, desc = null,
}) => {
    const cs = compact ? 24 : 30;
    const m = compact ? 8 : 14;
    const W = cols * cs + 2 * m;
    const H = rows * cs + 2 * m;
    const cx = (c) => m + (c - 0.5) * cs;
    const cy = (l) => m + (rows - l + 0.5) * cs;
    let f = '';
    // cases coloriées
    marquees.forEach(({ c, l }) => {
        f += `<rect x="${m + (c - 1) * cs}" y="${m + (rows - l) * cs}" width="${cs}" height="${cs}" fill="#93c5fd"/>`;
    });
    // quadrillage
    for (let i = 0; i <= cols; i++) {
        f += `<line x1="${m + i * cs}" y1="${m}" x2="${m + i * cs}" y2="${m + rows * cs}" stroke="#94a3b8" stroke-width="1"/>`;
    }
    for (let j = 0; j <= rows; j++) {
        f += `<line x1="${m}" y1="${m + j * cs}" x2="${m + cols * cs}" y2="${m + j * cs}" stroke="#94a3b8" stroke-width="1"/>`;
    }
    // repères de colonnes/lignes (petits chiffres)
    if (!compact) {
        for (let c = 1; c <= cols; c++) f += `<text x="${cx(c)}" y="${H - 2}" font-size="10" fill="#64748b" text-anchor="middle" font-family="Arial">${c}</text>`;
        for (let l = 1; l <= rows; l++) f += `<text x="${3}" y="${cy(l) + 3}" font-size="10" fill="#64748b" text-anchor="middle" font-family="Arial">${l}</text>`;
    }
    // départ + orientation
    if (showDepart && depart) {
        f += `<circle cx="${cx(depart.c)}" cy="${cy(depart.l)}" r="${cs * 0.28}" fill="#1E3A8A"/>`;
        f += `<polygon points="${FLECHE_DIR[orientation] || FLECHE_DIR.est}" fill="#f59e0b" transform="translate(${cx(depart.c)},${cy(depart.l)})"/>`;
    }
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial">`
        + (desc ? `<desc>${desc}</desc>` : '')
        + `<rect x="0" y="0" width="${W}" height="${H}" fill="#f8fafc"/>${f}</svg>`;
};
